import { AccountPickerModal } from "./AccountPickerModal";
import { fetchGA4Properties, selectGA4Property } from "./api";
import type { GA4PropertiesResponse } from "./types";

/**
 * GA4 property picker shown after the merchant connects Google Analytics from
 * the Fuentes tab. The OAuth callback returns with `?needs_picker=1` but the
 * credential has no property bound yet, so every GA read fails until one is
 * chosen. This modal closes that gap: list the visible GA4 properties, let the
 * merchant bind one, then refresh the dashboard. Without it the connection is a
 * dead, half-finished flow.
 *
 * A thin config over the shared `AccountPickerModal` — GA4 preselects the bound
 * property (re-connect) or the first one.
 */
export function GA4PropertyPickerModal({
  open,
  onClose,
  onSelected,
}: {
  open: boolean;
  onClose: () => void;
  onSelected: () => void;
}) {
  return (
    <AccountPickerModal<GA4PropertiesResponse>
      open={open}
      onClose={onClose}
      onSelected={onSelected}
      labelledById="ga4-picker-title"
      radioName="ga4-property"
      queryKey={["ga4-properties"]}
      fetchOptions={fetchGA4Properties}
      mapOptions={(data) =>
        data.properties.map((prop) => ({
          id: prop.property_id,
          label: prop.name,
          meta: `${prop.account_name} · ID ${prop.property_id}`,
        }))
      }
      preselect={(data) =>
        data.selected.property_id || data.properties[0]?.property_id || ""
      }
      selectOption={selectGA4Property}
      copy={{
        title: "Elegí la propiedad de Google Analytics",
        loading: "Buscando tus propiedades de GA4…",
        fetchError:
          "No pudimos leer tus propiedades de Google Analytics. Cerrá y volvé a intentar la conexión.",
        emptyError:
          "No encontramos propiedades de GA4 en esta cuenta de Google. Revisá que la cuenta tenga acceso a una propiedad de Analytics.",
        saveError: "No pudimos guardar la propiedad. Probá de nuevo.",
        confirmLabel: "Usar esta propiedad",
      }}
    />
  );
}
